import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ComposerPricingService } from '../services/composer-pricing.service';
import {TranslatePipe} from "@ngx-translate/core";
import {ComposerStateService} from "@app/model-composer";
import {AuthService} from "@service/auth/auth.service";

@Component({
  selector: 'app-composition-pricing',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="ipe:h-full ipe:min-h-0 ipe:flex ipe:flex-col ipe:bg-[#f6f3f1]">
      <div class="ipe:flex-1 ipe:min-h-0 ipe:overflow-y-auto ipe:border-t ipe:border-[#ede8e3]">
        <div class="ipe:max-w-3xl ipe:mx-auto ipe:px-6 ipe:py-8 ipe:flex ipe:flex-col ipe:gap-6">
          <div class="ipe:flex ipe:items-start ipe:justify-between ipe:gap-4">
            <div>
              <h2 class="ipe:text-sm  ipe:tracking-[0.15em] ipe:text-gray-400 font-walter">
                {{ 'RIEPILOGO' | translate }}
              </h2>
              <p class="ipe:text-lg ipe:text-gray-800 ipe:font-semibold font-walter">
                {{ 'PREZZO' | translate }}
              </p>
            </div>

            @if (pricing().kind === 'ready') {
              <div class="ipe:text-right">
                <div class="ipe:text-sm ipe:font-semibold ipe:text-black font-walter">
                  {{ totalLabel() }}
                </div>
              </div>
            }
          </div>

          <div class="ipe:flex ipe:flex-col ipe:gap-3">
            @if (pricing().kind === 'ready') {
              @for (row of rows(); track rowKey(row)) {
                <div class="
                  ipe:flex ipe:items-center ipe:justify-between ipe:gap-4
                  ipe:bg-white
                  ipe:border ipe:border-[#ede8e3]
                  ipe:rounded-sm
                  ipe:px-4 ipe:py-3
                ">
                  <div class="ipe:flex ipe:items-center ipe:gap-3 ipe:min-w-0">
                    <div class="ipe:w-14 ipe:h-14 ipe:rounded-md ipe:overflow-hidden ipe:bg-[#f6f3f1] ipe:shrink-0">
                      @if (row.image) {
                        <img [src]="row.image" class="ipe:w-full ipe:h-full ipe:object-cover"/>
                      }
                    </div>

                    <div class="ipe:min-w-0">
                      <div class="ipe:text-sm ipe:text-gray-800 ipe:font-medium">
                        {{ row.name }}
                      </div>
                      <div class="ipe:text-sm ipe:text-gray-400">
                        {{ row.dimensions }}
                      </div>
                      <div class="ipe:text-sm ipe:text-gray-400">
                        {{ row.code }}
                      </div>
                    </div>
                  </div>

                  <div class="ipe:flex ipe:items-center ipe:gap-3">
                    <span class="ipe:text-sm ipe:text-gray-400">Qtà</span>
                    <span class="
                      ipe:inline-flex ipe:items-center ipe:justify-center
                      ipe:min-w-10 ipe:h-8
                      ipe:px-3
                      ipe:rounded-md
                      ipe:bg-[#f6f3f1]
                      ipe:text-sm ipe:font-semibold ipe:text-gray-800
                    ">
                      {{ row.quantity }}
                    </span>
                  </div>
                </div>
              }
            } @else if (pricing().kind === 'loading') {
              <div class="ipe:text-sm ipe:text-gray-400">Calcolo riepilogo...</div>
            } @else if (pricing().kind === 'error') {
              <div class="ipe:text-sm ipe:text-red-500">{{ pricing().message }}</div>
            } @else {
              <div class="ipe:text-sm ipe:text-gray-400">Calcola il prezzo per vedere il riepilogo.</div>
            }
          </div>
        </div>
      </div>

      <div class="ipe:shrink-0 ipe:px-6 ipe:py-5 ipe:border-t ipe:border-[#ede8e3] ipe:bg-white">
        <div class="ipe:flex ipe:items-center ipe:justify-between ipe:gap-4">
          <div class="ipe:text-sm ipe:text-gray-400">
            @if (pricing().kind === 'ready') {
              Totale:
              <span class="ipe:text-gray-700 ipe:font-semibold">
                {{ totalLabel() }}
              </span>
            } @else {
              Calcola il prezzo per procedere.
            }
          </div>
          @if (addToProformaVisible()) {
            <button
              class="
              ipe:px-6 ipe:py-2
              ipe:text-sm  ipe:tracking-[0.15em]
              ipe:font-medium
              ipe:transition-all
              ipe:border
              font-walter
            "
              [disabled]="!canAddToProforma()"
              [class]="canAddToProforma()
              ? 'ipe:bg-[#000] ipe:text-white ipe:border-[#000] ipe:hover:bg-[#f6f3f1] ipe:cursor-pointer ipe:hover:text-gray-800'
              : 'ipe:bg-gray-100 ipe:text-gray-400 ipe:border-gray-200 ipe:cursor-not-allowed'
            "
              (click)="addToProforma()"
            >
              @if (oldCompositionId) {
                {{ 'AGGIORNA_PROFORMA' | translate }}
              } @else {
                {{ 'AGGIUNGI_PROFORMA' | translate }}
              }
            </button>

          }
        </div>
      </div>
    </div>
  `,
})
export class CompositionPricingComponent {
  private pricingSvc = inject(ComposerPricingService);
  private state = inject(ComposerStateService);
  private authService = inject(AuthService);
  readonly oldCompositionId = this.state.oldCompositionId();
  readonly pricing = this.pricingSvc.pricing;
  readonly rows = computed(() => {
    const state = this.pricing();
    if (state.kind !== 'ready') {
      return [];
    }
    return state?.value?.configurations ?? [];
  });
  readonly totalLabel = computed(() => {
    const state = this.pricing();
    if (state.kind !== 'ready') {
      return '—';
    }
    if (state.value.amountAll) {
      return state.value.amountAll;
    }
    return `${state.value?.configurations?.length ?? 0} elementi`;
  });
  readonly addToProformaVisible = computed(() => {
    return this.authService.currentUserPermission.canGoToUserArea;
  })
  readonly canAddToProforma = computed(() => {
    const state = this.pricing();
    return state.kind === 'ready' && this.rows().length > 0;
  });
  rowKey = (row: any) => `${row.uid ?? row.id}-${row.quantity}`;

  addToProforma(): void {
    this.pricingSvc.openAddToOrder();
  }
}
